import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  faCheck,
  faChevronDown,
  faMagnifyingGlass,
  faXmark
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from '~/components/Form/Input/TextInput.css'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { themeClass } from '~/styles/theme.css'
import {
  checkmark,
  dropDownValueBox,
  multiSelectContainer,
  openIcon,
  openIconOpen,
  optionItem,
  optionItemNotSelectable,
  optionsList,
  optionsPortal,
  optionsWrapper,
  removeTagIcon,
  searchInput,
  searchInputWrapper,
  selectedTag,
  selectedValue,
  selectedValueText,
  valueBoxActive,
  wrapper,
  wrapperInline
} from './Dropdown.css'
import {
  valueBoxFullWidth,
  valueBoxInline,
  valueBoxPlaceholder,
  valueBoxSmall
} from './Input.css'

export type DropdownOption = {
  label: string
  value: string
  element?: ReactElement
}

type BaseDropdownProps = InputInFormProps & {
  name?: string
  options?: DropdownOption[]
  optionsLoading?: boolean
  optionsInfiniteQuery?: UseInfiniteQueryResult<DropdownOption[] | undefined>
  onSearchChange?: (search: string | undefined) => void
  label?: string
  small?: boolean
  fullWidth?: boolean
  placeholder?: string
  emptyLabel?: string
  emptyMessage?: string
  valueBoxClassName?: string
  error?: null | { message: string }
  asMenu?: boolean
  searchable?: boolean
  required?: boolean
}

export type SingleDropdownProps = BaseDropdownProps & {
  multiple?: false
  defaultValue?: string | null
  defaultValues?: never
  onChange?: (value: string) => void
}

export type MultiDropdownProps = BaseDropdownProps & {
  multiple: true
  defaultValue?: never
  defaultValues?: string[] | null
  onChange?: (value: string[]) => void
}

export type DropdownProps = SingleDropdownProps | MultiDropdownProps

export const Dropdown = ({
  name,
  options,
  optionsLoading,
  optionsInfiniteQuery,
  onSearchChange,
  defaultValue,
  defaultValues,
  label,
  onChange,
  small = false,
  fullWidth = false,
  placeholder,
  emptyLabel,
  emptyMessage = 'Aucune option disponible',
  valueBoxClassName,
  register,
  watch,
  setValue,
  error = null,
  multiple = false,
  searchable = false,
  required = false,
  asMenu = false
}: DropdownProps): ReactElement => {
  if (!options && !optionsInfiniteQuery) {
    throw new Error('options or optionsInfiniteQuery is required')
  }
  const getValuesFromDefaultValues = useCallback(() => {
    if (multiple) {
      return defaultValues || []
    }
    return defaultValue !== undefined && defaultValue !== null
      ? [defaultValue]
      : []
  }, [defaultValue, defaultValues, multiple])

  const finalOptions = useMemo(() => {
    if (options) {
      if (emptyLabel) {
        return [
          {
            label: emptyLabel,
            value: ''
          },
          ...options
        ]
      }
      return options
    }
    if (optionsInfiniteQuery) {
      return optionsInfiniteQuery.data ?? []
    }
    return []
  }, [options, optionsInfiniteQuery, emptyLabel])

  const [selectedValues, setSelectedValues] = useState<string[]>(
    getValuesFromDefaultValues()
  )
  const labelRef = useRef<HTMLLabelElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState<string | undefined>(undefined)
  const [optionsSelectedStore, setOptionsSelectedStore] = useState<
    DropdownOption[]
  >([])

  const endOfListReached = useCallback(() => {
    if (
      optionsInfiniteQuery?.hasNextPage &&
      !optionsInfiniteQuery.isFetchingNextPage
    ) {
      optionsInfiniteQuery.fetchNextPage()
    }
  }, [optionsInfiniteQuery])

  // Use to trigger the infinite query when the last item is visible
  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached,
    enabled: optionsInfiniteQuery !== undefined
  })

  // Keep track of the passed query options to display them in valuebox
  // even if options have changed (ex: dropdown is open and value is not in the new options)
  useEffect(() => {
    setOptionsSelectedStore((current) => [
      ...current,
      ...(finalOptions.filter(
        (opt) => !current.includes(opt) && selectedValues.includes(opt.value)
      ) ?? [])
    ])
  }, [finalOptions, selectedValues])

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: (open) => {
      // If the dropdown is open, set empty search leads to fetch all options
      if (search === undefined) {
        setSearch('')
      }
      setIsOpen(open)
    },
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      if (labelRef.current?.contains(event.target as Node)) {
        return false
      }
      return true
    }
  })
  const role = useRole(context, { role: 'listbox' })

  // Merge props into a single object for convenience
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    dismiss,
    role
  ])

  if (register !== undefined && name !== undefined) {
    register(name)
  }

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(search)
    }
  }, [search, onSearchChange])

  useEffect(() => {
    if (name !== undefined && watch?.(name) !== undefined) {
      // If the value is already set, don't set default values
      return
    }
    setSelectedValues(getValuesFromDefaultValues())
    if (setValue && name) {
      setValue(
        name,
        multiple
          ? getValuesFromDefaultValues()
          : getValuesFromDefaultValues()[0]
      )
    }
  }, [getValuesFromDefaultValues, setValue, name, multiple, watch])

  const updateSelectedValues = (values: string[]) => {
    setSelectedValues(values)

    if (setValue && name) {
      setValue(name, multiple ? values : (values[0] ?? ''))
    }

    if (onChange && multiple) {
      ;(onChange as (value: string[]) => void)(values)
    } else if (onChange && !multiple) {
      ;(onChange as (value: string) => void)(values[0] ?? '')
    }
  }

  const handleSelection = (option: {
    label: string
    value: string
  }) => {
    let newValues: string[]
    if (!multiple && selectedValues.includes(option.value)) {
      return
    }

    if (selectedValues.includes(option.value)) {
      // If the value is already selected, remove it
      newValues = selectedValues.filter((v) => v !== option.value)
    } else if (!multiple) {
      // If the value is not selected and it's a single selection, set the value
      newValues = [option.value]
    } else {
      // Otherwise, add it
      newValues = [...selectedValues, option.value]
    }

    updateSelectedValues(newValues)

    if (!multiple) {
      setIsOpen(false)
    }
  }

  const removeValue = (value: string) => {
    let newValues = selectedValues.filter((v) => v !== value)
    if (newValues.length === 0 && emptyLabel !== undefined) {
      newValues = ['']
    }
    updateSelectedValues(newValues)
  }

  useEffect(() => {
    if (searchRef.current && isOpen) {
      searchRef.current.focus({ preventScroll: true })
    }
  }, [isOpen])

  const DropdownEl = (
    <>
      <div
        className={clsx(wrapper, !label && !fullWidth ? wrapperInline : null)}
      >
        <div
          ref={refs.setReference}
          className={clsx(
            dropDownValueBox,
            isOpen ? valueBoxActive : null,
            selectedValues.length === 0 && !asMenu ? valueBoxPlaceholder : null,
            small ? valueBoxSmall : null,
            fullWidth ? valueBoxFullWidth : null,
            !label ? valueBoxInline : null,
            valueBoxClassName,
            error !== null && inputError
          )}
          {...getReferenceProps()}
        >
          <div className={multiSelectContainer}>
            {selectedValues.length === 0 ? (
              <div className={selectedValueText}>{placeholder}</div>
            ) : (
              selectedValues.map((value) => {
                const canRemove =
                  (multiple || !required) &&
                  (emptyLabel === undefined || value !== '')
                return (
                  <div
                    key={value}
                    className={multiple ? selectedTag : selectedValue}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className={selectedValueText}>
                      {optionsSelectedStore.find((opt) => opt.value === value)
                        ?.label ??
                        (optionsInfiniteQuery?.isFetching
                          ? 'Chargement...'
                          : '')}
                    </div>
                    {canRemove && (
                      <FontAwesomeIcon
                        icon={faXmark}
                        className={removeTagIcon}
                        onClick={(e) => {
                          e.stopPropagation()
                          removeValue(value)
                        }}
                      />
                    )}
                  </div>
                )
              })
            )}
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={clsx(openIcon, isOpen ? openIconOpen : null)}
          />
        </div>
        <FloatingPortal>
          {isMounted && (
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={optionsPortal}
              {...getFloatingProps()}
            >
              <div
                style={{ ...transitionStyles }}
                className={clsx(themeClass, optionsWrapper)}
              >
                {(onSearchChange || searchable) && (
                  <div className={searchInputWrapper}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      ref={searchRef}
                      className={searchInput}
                      placeholder="Rechercher"
                    />
                  </div>
                )}
                <AnimatePresence>
                  <motion.div
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: { staggerChildren: 0.05 }
                      }
                    }}
                    initial="hidden"
                    animate="visible"
                    className={optionsList}
                  >
                    {finalOptions.length > 0 ? (
                      finalOptions
                        .filter((opt) =>
                          opt.label
                            .toLowerCase()
                            .includes(search?.toLowerCase() ?? '')
                        )
                        .map((option) => (
                          <motion.div
                            variants={{
                              initial: { opacity: 0, y: -20 },
                              hidden: { opacity: 0, y: -20 },
                              visible: { opacity: 1, y: 0 }
                            }}
                            key={option.value}
                            className={clsx(
                              optionItem,
                              !multiple && selectedValues.includes(option.value)
                                ? optionItemNotSelectable
                                : null
                            )}
                            onClick={() => handleSelection(option)}
                          >
                            {option.element ?? option.label}
                            {selectedValues.includes(option.value) &&
                              !asMenu && (
                                <span className={checkmark}>
                                  <FontAwesomeIcon icon={faCheck} />
                                </span>
                              )}
                          </motion.div>
                        ))
                    ) : (!optionsInfiniteQuery ||
                        !optionsInfiniteQuery.isFetching) &&
                      !optionsLoading ? (
                      <div
                        key="empty"
                        className={clsx(optionItem, optionItemNotSelectable)}
                      >
                        {emptyMessage}
                      </div>
                    ) : null}
                    {(optionsInfiniteQuery?.isFetching || optionsLoading) && (
                      <Loader small={true} forceText={true} key="loader" />
                    )}
                    {optionsInfiniteQuery && (
                      <div ref={lastItemRef} key="lastItemRef" />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
        </FloatingPortal>
      </div>
      {error !== null && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </>
  )

  if (!label) {
    return DropdownEl
  }
  return (
    <div className={inputWrapper}>
      <label
        htmlFor={name}
        className={inputLabel}
        ref={labelRef}
        onClick={() => setIsOpen(true)}
      >
        {label}
      </label>
      {DropdownEl}
    </div>
  )
}
